import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getAppealChain, getFinding } from "../data/index";
import { DisputeThreadTimeline } from "../components/DisputeThreadTimeline";
import { ErrorState, LoadingState } from "../components/Feedback";
import { humanizeIssueTag } from "../lib/workbenchFormat";

interface DisputeThreadBackState {
  backTo?: string;
  backLabel?: string;
}

export function DisputeThreadPage() {
  const { findingId = "" } = useParams<{ findingId: string }>();
  const { state } = useLocation();
  const back = (state as DisputeThreadBackState | null) ?? {};
  const backTo = back.backTo ?? "/reports/potential";
  const backLabel = back.backLabel ?? "Potential Claims";

  const findingQuery = useQuery({
    queryKey: ["findings", findingId],
    queryFn: ({ signal }) => getFinding(findingId, signal),
    enabled: Boolean(findingId),
  });

  const chainQuery = useQuery({
    queryKey: ["findings", findingId, "appeal-chain"],
    queryFn: ({ signal }) => getAppealChain(findingId, signal),
    enabled: Boolean(findingId),
  });

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link to={backTo} className="text-xs font-medium text-brand-700 hover:underline">
          ← Back to {backLabel}
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-slate-900">Dispute Thread</h1>
        {findingQuery.data ? (
          <p className="mt-1 text-sm text-slate-500">
            {humanizeIssueTag(findingQuery.data.issue_tag)} · {findingQuery.data.petitioner} v.{" "}
            {findingQuery.data.respondent}
          </p>
        ) : null}
      </div>

      {findingQuery.isLoading || chainQuery.isLoading ? <LoadingState label="Loading dispute thread..." /> : null}
      {findingQuery.isError ? <ErrorState error={findingQuery.error} label="Could not load finding" /> : null}
      {chainQuery.isError ? <ErrorState error={chainQuery.error} label="Could not load appeal chain" /> : null}

      {chainQuery.data ? (
        <div className="rounded-lg border border-slate-200 bg-white p-5">
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">
            {chainQuery.data.steps.length} step{chainQuery.data.steps.length === 1 ? "" : "s"} in this matter's
            appeal chain
          </p>
          <p className="mb-4 text-xs text-slate-400">
            Ordered chronologically (earliest decision first): this traces which forum ruled on the matter and
            when, so it always runs oldest to newest &mdash; unlike the Precedent tab, which orders by citation
            (who relied on whom), not date.
          </p>
          <DisputeThreadTimeline steps={chainQuery.data.steps} highlightFindingId={findingId} />
        </div>
      ) : null}
    </div>
  );
}
